"use client";

import { Box, Button, LinearProgress, Typography, Chip } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslations } from 'next-intl';
import React from 'react';
import { colors, typography } from '@/theme/colors';
interface StoreQuotaDisplayProps {
  used: number;
  limit: number;
  resetsAt: string;
  planName?: string;
}

/**
 * Displays store-wide API quota usage
 * All API keys share this quota pool
 */
const StoreQuotaDisplay: React.FC<StoreQuotaDisplayProps> = ({
  used,
  limit,
  resetsAt,
  planName
}) => {
  const t = useTranslations('apiKeys');
  const tPlans = useTranslations('apiPlans');
  const percentage = limit > 0 ? Math.min(used / limit * 100, 100) : 0;
  const remaining = Math.max(limit - used, 0);

  // Semantic color based on usage
  const getProgressColor = (): string => {
    if (percentage >= 100) return colors.status.error.main;
    if (percentage >= 80) return colors.status.warning.main;
    return colors.status.success.main;
  };

  // Format reset time
  const getResetText = (): string => {
    try {
      const resetDate = new Date(resetsAt);
      return formatDistanceToNow(resetDate, {
        addSuffix: true
      });
    } catch {
      return '';
    }
  };
  const handleUpgradeClick = () => {
    const section = document.getElementById('upgrade-section');
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <Box sx={{
    bgcolor: colors.background.paper,
    border: `1px solid ${colors.border.light}`,
    borderRadius: '12px',
    p: 3,
    mb: 3
  }} data-sentry-element="Box" data-sentry-component="StoreQuotaDisplay" data-sentry-source-file="StoreQuotaDisplay.tsx">
      {/* Header row */}
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2
    }} data-sentry-element="Box" data-sentry-source-file="StoreQuotaDisplay.tsx">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }} data-sentry-element="Box" data-sentry-source-file="StoreQuotaDisplay.tsx">
          <Typography sx={{
          ...typography.presets.bodyPrimary,
          fontWeight: 600,
          color: colors.text.primary
        }} data-sentry-element="Typography" data-sentry-source-file="StoreQuotaDisplay.tsx">
            {t('storeQuota')}
          </Typography>
          {planName && <Chip label={`${tPlans('currentPlan')}: ${planName}`} size="small" sx={{
          bgcolor: colors.status.success.light,
          color: colors.status.success.main,
          fontWeight: 600,
          fontSize: '0.75rem',
          border: `1px solid ${colors.status.success.main}`
        }} />}
        </Box>

        <Typography sx={{
        ...typography.presets.caption,
        color: colors.text.tertiary
      }} data-sentry-element="Typography" data-sentry-source-file="StoreQuotaDisplay.tsx">
          {t('resetsIn', {
          time: getResetText()
        })}
        </Typography>
      </Box>

      {/* Progress bar */}
      <LinearProgress variant="determinate" value={percentage} sx={{
      height: 10,
      borderRadius: '5px',
      bgcolor: colors.background.medium,
      mb: 1.5,
      '& .MuiLinearProgress-bar': {
        borderRadius: '5px',
        bgcolor: getProgressColor(),
        transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)'
      }
    }} data-sentry-element="LinearProgress" data-sentry-source-file="StoreQuotaDisplay.tsx" />

      {/* Usage stats */}
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }} data-sentry-element="Box" data-sentry-source-file="StoreQuotaDisplay.tsx">
        <Typography sx={{
        ...typography.presets.bodySecondary,
        color: colors.text.secondary
      }} data-sentry-element="Typography" data-sentry-source-file="StoreQuotaDisplay.tsx">
          <Box component="span" sx={{
          fontWeight: 600,
          color: colors.text.primary
        }} data-sentry-element="Box" data-sentry-source-file="StoreQuotaDisplay.tsx">
            {used.toLocaleString()}
          </Box>
          {' / '}
          {limit.toLocaleString()} {t('requestsUsed')}
        </Typography>

        <Typography sx={{
        ...typography.presets.bodySecondary,
        color: percentage >= 80 ? getProgressColor() : colors.text.tertiary,
        fontWeight: percentage >= 80 ? 600 : 400
      }} data-sentry-element="Typography" data-sentry-source-file="StoreQuotaDisplay.tsx">
          {remaining.toLocaleString()} {t('remaining')}
        </Typography>
      </Box>

      {percentage >= 80 && <Box sx={{
      mt: 2,
      pt: 2,
      borderTop: `1px solid ${colors.border.light}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      flexWrap: 'wrap'
    }}>
          <Typography sx={{
        ...typography.presets.bodySecondary,
        color: getProgressColor(),
        fontWeight: 600
      }}>
            {percentage >= 100 ? t('usageWarning100') : t('usageWarning80')}
          </Typography>
          <Button size="small" variant="contained" onClick={handleUpgradeClick} sx={{
        textTransform: 'none',
        bgcolor: colors.primary.main,
        minHeight: 36,
        px: 2,
        '&:hover': {
          bgcolor: colors.primary.hover
        }
      }}>
            {t('upgradeNow')}
          </Button>
        </Box>}

      {/* Info text about shared quota */}
      <Typography sx={{
      ...typography.presets.caption,
      color: colors.text.tertiary,
      mt: 2,
      fontStyle: 'italic'
    }} data-sentry-element="Typography" data-sentry-source-file="StoreQuotaDisplay.tsx">
        {t('sharedQuotaInfo')}
      </Typography>
    </Box>;
};
export default StoreQuotaDisplay;